import { Component, ViewChild, TemplateRef, Output, EventEmitter, OnDestroy, Input, OnChanges, SimpleChanges, NgZone, OnInit } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, skip } from 'rxjs/operators';
import { MagicSearchModel, AdvancedFilterModel, stemsEQModel } from 'src/app/models/magic-search.model';
import { AudioAttachment } from './magic-search-audio-modal/magic-search-audio-modal.component';
import { DEFAULT_FILTERS, DEFAULT_STEMS } from 'src/app/constants/magic-search.constants';
import { CommonService } from 'src/app/services/Common-function/common.service';
import { PlayerService } from 'src/app/services/music-player/player.service';
import { BrowserStorageService } from 'src/app/services/browser-storage/browser-storage.service';
import { Ga4trackingService } from '../../../services/ga4Tracking/ga4tracking.service';
import { MagicSearchService } from 'src/app/services/magic-search/magic-search.service';

@Component({
  selector: 'ipm-magic-search',
  templateUrl: './magic-search.component.html',
  styleUrls: ['./magic-search.component.scss'],
})
export class MagicSearchComponent implements OnInit, OnDestroy, OnChanges {
  @ViewChild('audioDialog', { static: false }) audioDialog: TemplateRef<unknown>;
  @ViewChild('lyricsDialog', { static: false }) lyricsDialog: TemplateRef<unknown>;
  @Input() initialPayload: MagicSearchModel | null = null;
  @Input() compact = false;
  @Output() searchRequest = new EventEmitter<MagicSearchModel>();
  @Output() resetRequest = new EventEmitter<void>();
  @Output() filtersChange = new EventEmitter<AdvancedFilterModel>();
  @Input() lyricsOpen = false;

  queryText = '';
  lyricsText = '';
  lyricsDraftText = '';
  audio: AudioAttachment | null = null;
  audioUrl: string | null = null;
  isPlaying = false;
  chipBars: number[] = [];
  playbackProgress = 0;   // 0–100
  get playedBarCount(): number { return Math.round(this.playbackProgress / 100 * this.chipBars.length); }
  private audioEl: HTMLAudioElement | null = null;
  private rafId: number | null = null;
  private readonly BAR_COUNT = 60;

  filters: AdvancedFilterModel = { ...DEFAULT_FILTERS };
  stems: stemsEQModel = { ...DEFAULT_STEMS, levels: { ...DEFAULT_STEMS.levels } };
  eqEnabled = true;

  isListening = false;
  isPaused = false;
  private sessionBaseline = '';
  voiceBars: number[] = Array(90).fill(0.04);
  private recognition: any = null;
  // Visualizer (mirrors audio modal approach)
  private audioCtx: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private analyserData: Uint8Array | null = null;
  private vizRafId: number | null = null;
  private smoothVolume = 0;
  private runningPeak = 0.05;
  private lastSampleTs = 0;
  private readonly VIZ_BAR_COUNT = 30;
  private get vizBarCount(): number {
    return this.commonService.isMobileDisableClick ? 60 : 90;
  }
  private readonly VIZ_SAMPLE_MS = 150;

  private readonly placeholders = [
    'Describe your sound...',
    '"Tabla over dark trap..."',
    '"Sitar with dreamy R&B..."',
    '"Punjabi folk meets drill..."',
    'Describe your sound — dark trap 808s, lofi piano sad…',
    'Paste lyrics to find matching beats…',
    'Hum or sing to find matching beats…',
    'Upload a reference track to find similar beats…',
    'Paste a song link to find similar beats…',
  ];
  private placeholderIndex = 0;
  private placeholderTimer: any;
  currentPlaceholder = this.placeholders[0];
  placeholderFading = false;

  async startSpeechSearch(): Promise<void> {
    this.pauseBeatPlayerIfPlaying();
    let eventName = 'click_speech_search';
    let attributes = {
      label : 'Engagement',
      action : 'click',
      category : 'magic search',
      userId : this.browserService.getUserId(),
      userRole : this.browserService.getCurrentRoles()
    }
    this.Ga4Service.sendEvent(eventName , attributes);
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) return;

    this.isListening = true;
    this.isPaused = false;

    try {
      this.micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      this.isListening = false;
      return;
    }

    const rec = new SpeechRecognition();
    rec.lang = 'en-IN';
    rec.interimResults = true;
    rec.continuous = true;
    this.recognition = rec;

    // Baseline carries text already in the box (from previous sessions or typing)
    this.sessionBaseline = this.queryText;

    let firstStart = true;
    rec.onstart = () => {
      this.startVoiceBarsAnimation(this.micStream!, !firstStart);
      firstStart = false;
    };

    // Write all results directly — prepend baseline so restarts don't wipe previous text
    rec.onresult = (event: any) => {
      let transcript = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        transcript += event.results[i][0].transcript;
      }
      
      this.ngZone.run(() => {
        this.queryText = transcript.trim();
      });
    };

    rec.onerror = (e: any) => {
      console.error('Speech recognition error:', e.error);
      if (e.error === 'network') {
        this.stopSpeech();
      } else if (e.error !== 'no-speech' && e.error !== 'aborted') {
        this.stopSpeech();
      }
    };

    // Browser ended on silence — restart; update baseline so new session appends
    rec.onend = () => {
      // Always sync baseline to current text so next session appends correctly
      this.sessionBaseline = this.queryText;
      
      if (this.isListening && !this.isPaused && this.recognition === rec) {
        try { 
          rec.start(); 
        } catch (err) { 
          console.error('Failed to restart speech:', err);
          this.stopSpeech(); 
        }
      }
    };

    rec.start();
    this.commonService.setSpeechListening(true);
  }

  private micStream: MediaStream | null = null;

  pauseSpeech(): void {
    if (this.isListening && !this.isPaused) {
      this.isPaused = true;
      if (this.recognition) {
        this.recognition.stop();
      }
      this.stopVoiceBarsAnimation(true);
    }
  }

  resumeSpeech(): void {
    if (this.isListening && this.isPaused) {
      this.isPaused = false;
      this.sessionBaseline = this.queryText;
      if (this.recognition) {
        try {
          this.recognition.start();
        } catch (e) {
          this.stopSpeech();
        }
      }
      if (this.micStream) {
        this.startVoiceBarsAnimation(this.micStream, true);
      }
    }
  }

  stopSpeech(): void {
    this.isListening = false;
    this.isPaused = false;
    this.stopVoiceBarsAnimation();
    if (this.micStream) { this.micStream.getTracks().forEach(t => t.stop()); this.micStream = null; }
    const rec = this.recognition;
    this.recognition = null;
    if (rec) { try { rec.stop(); } catch { /* ignore */ } }
    this.commonService.setSpeechListening(false);
  }

  private startVoiceBarsAnimation(stream: MediaStream, isResume = false): void {
    this.stopVoiceBarsAnimation(isResume); // teardown any existing context first
    if (!isResume) {
      this.voiceBars = new Array(this.vizBarCount).fill(0.04);
      this.smoothVolume = 0;
      this.runningPeak = 0.05;
    }
    this.lastSampleTs = 0;
    try {
      const Ctor: any = (window as any).AudioContext || (window as any).webkitAudioContext;
      const ctx: AudioContext = new Ctor();
      this.audioCtx = ctx;
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.8;
      source.connect(analyser);
      this.analyser = analyser;
      this.analyserData = new Uint8Array(analyser.frequencyBinCount);
    } catch { this.analyser = null; }

    this.ngZone.runOutsideAngular(() => {
      const tick = (ts: number) => {
        if (!this.isListening || this.isPaused) return;
        if (this.analyser && this.analyserData) {
          this.analyser.getByteTimeDomainData(this.analyserData as any);
          let sum = 0;
          for (let i = 0; i < this.analyserData.length; i++) {
            const s = (this.analyserData[i] - 128) / 128;
            sum += s * s;
          }
          const rms = Math.sqrt(sum / this.analyserData.length);
          this.smoothVolume += (rms - this.smoothVolume) * 0.25;
          if (ts - this.lastSampleTs >= this.VIZ_SAMPLE_MS) {
            this.lastSampleTs = ts;
            this.runningPeak = this.smoothVolume > this.runningPeak
              ? this.smoothVolume
              : Math.max(0.04, this.runningPeak * 0.985);
            const norm = this.smoothVolume / this.runningPeak;
            const level = Math.min(0.95, Math.max(0.08, Math.sqrt(norm) * 0.92));
            const next = this.voiceBars.slice(1);
            next.push(level);
            this.ngZone.run(() => { this.voiceBars = next; });
          }
        }
        this.vizRafId = requestAnimationFrame(tick);
      };
      this.vizRafId = requestAnimationFrame(tick);
    });
  }

  private stopVoiceBarsAnimation(keepBars = false): void {
    if (this.vizRafId !== null) { cancelAnimationFrame(this.vizRafId); this.vizRafId = null; }
    if (this.audioCtx) { try { this.audioCtx.close(); } catch { /* noop */ } this.audioCtx = null; }
    this.analyser = null;
    this.analyserData = null;
    if (!keepBars) {
      this.voiceBars = new Array(this.vizBarCount).fill(0.04);
    }
  }

  constructor(
    public commonService: CommonService, 
    private ngZone: NgZone, 
    private playerService: PlayerService,
    public browserService : BrowserStorageService,
    public magicSearchService: MagicSearchService,
    private Ga4Service : Ga4trackingService ) {
    this.placeholderTimer = setInterval(() => {
      this.placeholderFading = true;
      setTimeout(() => {
        this.placeholderIndex = (this.placeholderIndex + 1) % this.placeholders.length;
        this.currentPlaceholder = this.placeholders[this.placeholderIndex];
        this.placeholderFading = false;
      }, 400);
    }, 3000);
  }

  ngOnInit(): void {
    this.filterSub = this.filterChange$.pipe(debounceTime(500)).subscribe(() => this.runSearch());

    // If the beat player starts while speech recognition is active, yield —
    // don't let the two audio sources run at once.
    this.speechMusicSub = this.commonService.isMusicPlaying$.subscribe((isPlaying) => {
      if (isPlaying && this.isListening) {
        this.stopSpeech();
      }
    });
    
    this.magicSearchService.setStemsEq(this.stems, this.eqEnabled);
    this.stemsEqSub = this.magicSearchService.stemsEq$.pipe(skip(1)).subscribe(({ stems, eqEnabled }) => {
      if (stems === this.stems && eqEnabled === this.eqEnabled) return;
      this.stems = stems;
      this.eqEnabled = eqEnabled;
      this.filterChange$.next();
    });
    this.filtersUpdateSub = this.magicSearchService.filtersUpdateFromFilterRow$.subscribe((f) => {
      this.filters = f;
      this.filterChange$.next();
    });

    this.magicSearchService.setAttachmentsState(this.lyricsOpen, this.audio?.name ?? null);
    this.magicSearchService.setLyricsText(this.lyricsText);
    this.removeLyricsSub = this.magicSearchService.removeLyricsRequest$.subscribe(() => this.removeLyrics());
    this.removeAudioSub = this.magicSearchService.removeAudioRequest$.subscribe(() => this.removeAudio());
    this.clearQueryTextSub = this.magicSearchService.clearQueryTextRequest$.subscribe(() => {
      this.queryText = '';
    });
    this.togglePlaybackSub = this.magicSearchService.togglePlaybackRequest$.subscribe(() => this.togglePlayAudio());
    this.openLyricsDialogSub = this.magicSearchService.openLyricsDialogRequest$.subscribe(() => this.openLyricsModal());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialPayload'] && this.initialPayload) {
      this.hydrateFromPayload(this.initialPayload);
    }
  }

  private hydrateFromPayload(payload: MagicSearchModel): void {
    this.queryText = payload.query_text ?? '';

    if (payload.lyrics_text && payload.lyrics_text.trim().length > 0) {
      this.lyricsOpen = true;
      this.lyricsText = payload.lyrics_text;
    } else {
      this.lyricsOpen = false;
      this.lyricsText = '';
    }

    if (payload.query_audio && payload.query_audio.trim().length > 0) {
      const val = payload.query_audio.trim();
      const isUrl = /^https?:\/\//i.test(val);
      if (isUrl) {
        this.audio = { type: 'url', name: val, fingerprint: false };
        this.audioUrl = val;
        this.chipBars = this.fallbackBars();
      } else {
        const live = this.magicSearchService.attachedAudio;
        if (live?.blob) {
          this.setAudio(live);
        } else {
          this.audio = { type: 'recording', name: 'Attached audio', fingerprint: false, base64: val };
        }
      }
    } else {
      this.audio = null;
    }

    this.filters = payload.filters
      ? { ...DEFAULT_FILTERS, ...payload.filters, stems: null }
      : { ...DEFAULT_FILTERS };
    this.stems = payload.filters?.stems
      ? {
          levels: { ...DEFAULT_STEMS.levels, ...payload.filters.stems.levels },
          muted: payload.filters.stems.muted ?? null,
        }
      : { ...DEFAULT_STEMS, levels: { ...DEFAULT_STEMS.levels } };
    this.eqEnabled = true;

    this.filtersChange.emit(this.filters);
    this.magicSearchService.setStemsEq(this.stems, this.eqEnabled);
    this.magicSearchService.setAttachmentsState(this.lyricsOpen, this.audio?.name ?? null);
    this.magicSearchService.setLyricsText(this.lyricsText);
  }


  get detectedModes(): string[] {
    const modes: string[] = [];
    if (this.queryText.trim() || this.isListening) modes.push('Natural Language');
    if (this.lyricsOpen && this.lyricsText.trim()) modes.push('Lyrics');
    if (this.audio) modes.push('Vocal');
    return modes;
  }

  get detectedLabel(): string {
    const m = this.detectedModes;
    if (m.length === 0) return '';
    if (m.length === 1) return m[0];
    return 'Combined';
  }

  async runSearch(): Promise<void> {
    const payload: MagicSearchModel = {
      query_text: this.queryText.trim() || null,
      lyrics_text: (this.lyricsOpen && this.lyricsText.trim()) ? this.lyricsText.trim() : null,
      query_audio: await this.resolveQueryAudio(),
      filters: this.buildFilters(),
    };
    this.searchRequest.emit(payload);
  }

  /**
   * Resolves the audio attachment to the wire format for `query_audio`:
   *  - recording / upload: raw base64 (no data-URL prefix) of the audio bytes
   *  - url: the URL string as-is (backend fetches it)
   *  - none: null
   */
  private async resolveQueryAudio(): Promise<string | null> {
    if (!this.audio) return null;
    if (this.audio.blob) return this.blobToBase64(this.audio.blob);
    if (this.audio.base64) return this.audio.base64;
    if (this.audio.type === 'url') return this.audio.name || null;
    return null;
  }

  private blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(reader.error);
      reader.onload = () => {
        const result = reader.result as string;
        // Strip the `data:<mime>;base64,` prefix to return raw base64.
        const commaIdx = result.indexOf(',');
        resolve(commaIdx >= 0 ? result.slice(commaIdx + 1) : result);
      };
      reader.readAsDataURL(blob);
    });
  }

  private buildFilters(): AdvancedFilterModel | null {
    const f = this.filters;
    const stemsPayload = this.eqEnabled ? this.stems : null;
    const hasFilter =
      f.genre_whitelist?.length ||
      f.mood?.length ||
      f.key ||
      f.bpm_min !== null ||
      f.bpm_max !== null ||
      f.price_max !== null ||
      stemsPayload !== null;
    if (!hasFilter) return null;
    return { ...f, stems: stemsPayload };
  }

  private filterChange$ = new Subject<void>();
  private filterSub: Subscription;
  private speechMusicSub: Subscription;
  private stemsEqSub: Subscription;
  private filtersUpdateSub: Subscription;
  private removeLyricsSub: Subscription;
  private removeAudioSub: Subscription;
  private clearQueryTextSub: Subscription;
  private togglePlaybackSub: Subscription;
  private openLyricsDialogSub: Subscription;

  selectAttachOption(tab: 'record' | 'upload'): void {
    this.openAudioModal(tab, tab === 'upload');
  }

  openAudioModal(tab: 'record' | 'upload' = 'record', fingerprint = false): void {
    //Event 
    let eventName = (tab === 'record') ? 'click_upload_blob' : 'click_upload_file';
    let attributes = {
      label: 'Engagement',
      action : 'click',
      category : 'magic search',
      userRole : this.browserService.getCurrentRoles(),
      userId : this.browserService.getUserId()
    }
    this.Ga4Service.sendEvent(eventName , attributes);
    
    // this.pauseBeatPlayerIfPlaying();
    const className = this.commonService.isMobileDisableClick
      ? 'mobile-view-magic-search-audio-model'
      : 'magic-search-audio-model';
    this.commonService.openModel(this.audioDialog, { fingerprint, activetab: tab }, className);
    const result$ = this.commonService.isMobileDisableClick
      ? this.commonService.bottomRef.afterDismissed()
      : this.commonService.modelRef.afterClosed();
    result$.subscribe((attachment: AudioAttachment | null) => {
      if (attachment) {
        this.setAudio(attachment);
      }
    });
  }

  attachFingerprintAudio(): void {
    this.openAudioModal('upload', true);
  }

  removeAudio(): void {
    this.stopAudio();
    this.audio = null;
    if (this.audioUrl) { URL.revokeObjectURL(this.audioUrl); this.audioUrl = null; }
    this.magicSearchService.setAttachmentsState(this.lyricsOpen, null);
    this.magicSearchService.setAttachedAudio(null);
  }

  private setAudio(attachment: AudioAttachment): void {
    this.removeAudio();
    this.audio = attachment;
    if (attachment.blob) {
      this.audioUrl = URL.createObjectURL(attachment.blob);
      this.decodeWaveform(attachment.blob);
    } else if (attachment.type === 'url') {
      this.audioUrl = attachment.name;
      this.chipBars = this.fallbackBars();
    }
    this.magicSearchService.setAttachmentsState(this.lyricsOpen, this.audio?.name ?? null);
    this.magicSearchService.setAttachedAudio(attachment);
  }

  /** Commits whatever's currently in lyricsText (used by the lyrics dialog's Save). */
  saveLyrics(): void {
    this.lyricsOpen = !!this.lyricsText.trim();
    this.magicSearchService.setAttachmentsState(this.lyricsOpen, this.audio?.name ?? null);
    this.magicSearchService.setLyricsText(this.lyricsText);
  }

  /** Compact bar's lyrics chip ✕. */
  removeLyrics(): void {
    this.lyricsOpen = false;
    this.lyricsText = '';
    this.magicSearchService.setAttachmentsState(false, this.audio?.name ?? null);
    this.magicSearchService.setLyricsText('');
  }

  /** Opens the lyrics dialog for either adding new lyrics or editing what's attached. */
  openLyricsModal(): void {
    this.lyricsDraftText = this.lyricsText;
    this.commonService.openModel(this.lyricsDialog, {}, 'ipm-lyrics-edit-dialog');
  }

  saveLyricsModal(): void {
    this.lyricsText = this.lyricsDraftText;
    this.saveLyrics();
    this.commonService.closePopUp();
  }

  removeLyricsModal(): void {
    this.removeLyrics();
    this.commonService.closePopUp();
  }

  private async decodeWaveform(blob: Blob): Promise<void> {
    try {
      const Ctor: any = (window as any).AudioContext || (window as any).webkitAudioContext;
      const ctx = new Ctor();
      const buf = await blob.arrayBuffer();
      const decoded = await ctx.decodeAudioData(buf);
      ctx.close();
      const data = decoded.getChannelData(0);
      const step = Math.floor(data.length / this.BAR_COUNT);
      this.chipBars = Array.from({ length: this.BAR_COUNT }, (_, i) => {
        let sum = 0;
        for (let j = 0; j < step; j++) sum += Math.abs(data[i * step + j]);
        return (sum / step) * 100;
      });
      // Normalise so tallest bar = 100%
      const max = Math.max(...this.chipBars, 0.01);
      this.chipBars = this.chipBars.map(v => Math.max(8, (v / max) * 100));
    } catch {
      this.chipBars = this.fallbackBars();
    }
  }

  private fallbackBars(): number[] {
    return Array.from({ length: this.BAR_COUNT }, (_, i) => Math.max(8, 10 + ((i * 37) % 60)));
  }

  /**
   * Pause the beat player if it's currently playing. Used before mic/audio
   * capture so the two audio sources don't overlap. No-op when nothing is
   * playing — uses the mirrored flag on CommonService to avoid a fresh
   * subscription on every trigger.
   */
  private pauseBeatPlayerIfPlaying(): void {
    if (this.commonService.isMusicPlaying$.value) {
      this.playerService.pause();
    }
  }

  togglePlayAudio(): void {
    if (!this.audioUrl) return;
    if (!this.audioEl) {
      this.audioEl = new Audio(this.audioUrl);
      this.audioEl.onended = () => {
        this.isPlaying = false;
        this.playbackProgress = 0;
        this.cancelRaf();
        this.magicSearchService.setAudioPlayingState(false);
      };
    }
    if (this.isPlaying) {
      this.audioEl.pause();
      this.isPlaying = false;
      this.cancelRaf();
    } else {
      this.audioEl.play();
      this.isPlaying = true;
      this.tickProgress();
    }
    this.magicSearchService.setAudioPlayingState(this.isPlaying);
  }

  private tickProgress(): void {
    const el = this.audioEl;
    if (!el || !this.isPlaying) return;
    this.playbackProgress = el.duration ? (el.currentTime / el.duration) * 100 : 0;
    this.rafId = requestAnimationFrame(() => this.tickProgress());
  }

  private cancelRaf(): void {
    if (this.rafId !== null) { cancelAnimationFrame(this.rafId); this.rafId = null; }
  }

  private stopAudio(): void {
    this.cancelRaf();
    if (this.audioEl) { this.audioEl.pause(); this.audioEl = null; }
    this.isPlaying = false;
    this.playbackProgress = 0;
    this.magicSearchService.setAudioPlayingState(false);
  }

  toggleLyrics(): void {
    if(!this.lyricsOpen){
      let eventName = 'click_add_lyrics';
      let attributes = {
        label : 'Engagement',
        action: 'click',
        category: 'magic search',
        userRole : this.browserService?.getCurrentRoles(),
        userId : this.browserService?.getUserId(),
      }
      this.Ga4Service.sendEvent(eventName , attributes);
    }
    this.lyricsOpen = !this.lyricsOpen;
    if (!this.lyricsOpen) this.lyricsText = '';
  }

  reset(): void {
    this.queryText = '';
    this.lyricsOpen = false;
    this.lyricsText = '';
    this.removeAudio();
    this.filters = { ...DEFAULT_FILTERS };
    this.stems = { ...DEFAULT_STEMS, levels: { ...DEFAULT_STEMS.levels } };
    this.eqEnabled = true;
    this.filtersChange.emit(this.filters);
    this.magicSearchService.setStemsEq(this.stems, this.eqEnabled);
    this.magicSearchService.setLyricsText('');
    this.resetRequest.emit();
  }

  trackByIndex(index: number): number { return index; }

  onSearchModeChange(mode: 'classic' | 'magic'): void {
    this.magicSearchService.setSearchMode(mode);
  }

  onQueryChange(val: string): void {
    if (!val) return;
    const lines = val.split('\n').length;
    // Heuristic: >100 lines or >1000 chars is likely lyrics
    if (lines >= 100 || val.length > 1000) {
      this.lyricsText = val;
      this.lyricsOpen = true;
      // Use timeout to ensure the textarea is cleared in the next tick
      setTimeout(() => {
        this.queryText = '';
      });
    }
  }

  onEnter(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey && !this.isListening) {
      event.preventDefault();
      this.runSearch();
    }
  }

  ngOnDestroy(): void {
    this.filterSub?.unsubscribe();
    this.speechMusicSub?.unsubscribe();
    this.stemsEqSub?.unsubscribe();
    this.filtersUpdateSub?.unsubscribe();
    this.removeLyricsSub?.unsubscribe();
    this.removeAudioSub?.unsubscribe();
    this.clearQueryTextSub?.unsubscribe();
    this.togglePlaybackSub?.unsubscribe();
    this.openLyricsDialogSub?.unsubscribe();
    this.stopAudio();
    this.stopSpeech();
    if (this.audioUrl) URL.revokeObjectURL(this.audioUrl);
    clearInterval(this.placeholderTimer);
  }
}
